import { Chessground } from '@lichess-org/chessground';
import type { Config } from '@lichess-org/chessground/config';
import type { Api } from '@lichess-org/chessground/api';
import type { Color, Dests, Key } from '@lichess-org/chessground/types';

import type { MoveIntent, RulesSnapshot } from '../rules';
import type {
  BoardIntentHandler,
  BoardOrientation,
  BoardPort,
  BoardProjection,
  BoardSquare,
} from './boardPort';

/** The small Chessground surface used by this adapter exposes writes and cleanup only. */
export interface ChessgroundBoard {
  set(config: Config): void;
  destroy(): void;
}

export type ChessgroundBoardFactory = (
  element: HTMLElement,
  config: Config,
) => ChessgroundBoard;

export interface ChessgroundBoardPortOptions {
  readonly element: HTMLElement;
  /** The first configuration is required so Chessground never invents a position. */
  readonly initialProjection: BoardProjection;
  /** Must validate through the owning session and return its authoritative projection. */
  readonly onMoveIntent: BoardIntentHandler;
  /** Allows deterministic tests without a DOM-backed Chessground instance. */
  readonly createBoard?: ChessgroundBoardFactory;
}

function createChessground(element: HTMLElement, config: Config): ChessgroundBoard {
  return Chessground(element, config) as Pick<Api, 'set' | 'destroy'>;
}

function toChessgroundColor(color: BoardOrientation): Color {
  return color;
}

function toKey(square: BoardSquare): Key {
  return square as Key;
}

/**
 * Projects every legal coordinate move into Chessground's source-to-dest map.
 * Promotion variants deliberately converge on the same square: selection of
 * q/r/b/n remains an explicit session/UI action, never a Chessground choice.
 */
export function legalDestsFromSnapshot(snapshot: RulesSnapshot): Dests {
  const destinations = new Map<Key, Key[]>();

  for (const move of snapshot.legalMoves) {
    const from = toKey(move.from);
    const to = toKey(move.to);
    const existing = destinations.get(from);

    if (existing === undefined) {
      destinations.set(from, [to]);
    } else if (!existing.includes(to)) {
      existing.push(to);
    }
  }

  return destinations;
}

/** Returns the exact interaction policy shared by initial and update configs. */
export function boardInputEnabled(projection: BoardProjection): boolean {
  return (
    projection.interactive &&
    !projection.promotionPending &&
    !projection.review &&
    !projection.snapshot.result.terminal
  );
}

interface ConfigCallbacks {
  readonly afterMove: (from: Key, to: Key) => void;
}

/**
 * Converts a CapyChess projection into a safe, complete Chessground config.
 * The adapter supplies no labels, markup, or drawing shapes.
 */
export function chessgroundConfigFromProjection(
  projection: BoardProjection,
  callbacks: ConfigCallbacks,
): Config {
  const inputEnabled = boardInputEnabled(projection);
  const dests = inputEnabled
    ? legalDestsFromSnapshot(projection.snapshot)
    : new Map<Key, Key[]>();

  return {
    // RulesSnapshot.fen is the only position source. Chessground is never read.
    fen: projection.snapshot.fen,
    orientation: projection.orientation,
    turnColor: projection.snapshot.turn === 'w' ? 'white' : 'black',
    coordinates: projection.coordinates,
    selected: projection.selectedSquare === undefined
      ? undefined
      : toKey(projection.selectedSquare),
    lastMove: projection.lastMove === undefined
      ? undefined
      : [toKey(projection.lastMove.from), toKey(projection.lastMove.to)],
    check: projection.checkedColor === undefined
      ? false
      : toChessgroundColor(projection.checkedColor),
    // Keep bindings installed: Chessground cannot change viewOnly after creation.
    viewOnly: false,
    autoCastle: false,
    highlight: {
      lastMove: true,
      check: true,
    },
    // Positioning remains Chessground-owned, while move animation stays off.
    animation: {
      enabled: false,
    },
    movable: {
      free: false,
      color: inputEnabled
        ? projection.snapshot.turn === 'w'
          ? 'white'
          : 'black'
        : undefined,
      dests,
      showDests: inputEnabled,
      rookCastle: false,
      events: {
        after: callbacks.afterMove,
      },
    },
    premovable: {
      enabled: false,
      showDests: false,
      castle: false,
    },
    predroppable: {
      enabled: false,
    },
    draggable: {
      enabled: inputEnabled,
      deleteOnDropOff: false,
      showGhost: false,
    },
    selectable: {
      enabled: inputEnabled,
    },
    // Do not pass any SVG/HTML labels or arbitrary user/imported strings.
    drawable: {
      enabled: false,
      visible: false,
      shapes: [],
      autoShapes: [],
    },
  };
}

/**
 * A strict Chessground projection. It does not own a RulesPort, submit moves,
 * retain partial FEN, or make a promotion decision. Every move attempt returns
 * through the session and is immediately redrawn from its returned snapshot.
 */
export class ChessgroundBoardPort implements BoardPort {
  private readonly board: ChessgroundBoard;
  private readonly onMoveIntent: BoardIntentHandler;
  private projection: BoardProjection;
  private destroyed = false;

  public constructor(options: ChessgroundBoardPortOptions) {
    this.projection = options.initialProjection;
    this.onMoveIntent = options.onMoveIntent;

    const initialConfig = this.configFor(options.initialProjection);
    this.board = (options.createBoard ?? createChessground)(
      options.element,
      initialConfig,
    );
  }

  public render(projection: BoardProjection): void {
    if (this.destroyed) {
      return;
    }

    this.projection = projection;
    this.board.set(this.configFor(projection));
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.board.destroy();
  }

  private configFor(projection: BoardProjection): Config {
    const expectedRevision = projection.snapshot.revision;

    return chessgroundConfigFromProjection(projection, {
      afterMove: (from, to) => {
        this.handleMoveIntent(expectedRevision, from, to);
      },
    });
  }

  private handleMoveIntent(
    expectedRevision: number,
    from: Key,
    to: Key,
  ): void {
    // An old event may fire after a newer projection or after unmount.
    if (this.destroyed) {
      return;
    }

    if (
      this.projection.snapshot.revision !== expectedRevision ||
      !boardInputEnabled(this.projection)
    ) {
      // Chessground has already applied its transient local move before `after`.
      // Even a stale/now-locked callback must restore the current authority.
      this.render(this.projection);
      return;
    }

    const intent: MoveIntent = {
      expectedRevision,
      from,
      to,
    };
    let authoritativeProjection = this.projection;

    try {
      authoritativeProjection = this.onMoveIntent(intent);
    } finally {
      // Rejects and accepts alike discard Chessground's transient local move.
      if (!this.destroyed) {
        this.render(authoritativeProjection);
      }
    }
  }
}
